import { Injectable, inject, signal, effect, NgZone } from '@angular/core';
import { SocketService } from '../../core/websocket/socket.service';
import { DevicesService } from './devices.service';
import { BotConnectionEvent } from './models/connectionsEvent';
import { mapOnlineData } from './mappers/online-status.mapper';
import { Device } from './bots.component';

@Injectable({
  providedIn: 'root',
})
export class DeviceWebSocketService {
  private socketService = inject(SocketService);
  private devicesService = inject(DevicesService);

  // Signals
  connections = signal<BotConnectionEvent | null>(null);
  credentials = signal<any | null>(null);
  errors = signal<string | null>(null);
  private ngZone = inject(NgZone);
  private peerConnections: Record<string, RTCPeerConnection> = {};
  private iceCandidateQueue: Record<string, RTCIceCandidateInit[]> = {};
  

  private subscriptions: any[] = [];

  constructor() {
    // Wait for socket to be connected before subscribing
    effect(() => {
      const status = this.socketService.status();

      if (status === 'connected') {
        // Set up subscriptions only when connected
        this.setupSubscriptions();
      } else if (status === 'disconnected') {
        // Clean up subscriptions when disconnected
        this.cleanupSubscriptions();
      }
    });
  }

  private setupSubscriptions(): void {
    // Clean up any existing subscriptions first
    this.cleanupSubscriptions();

    // Subscribe to connection updates
    const connectionsSub = this.socketService.on<BotConnectionEvent>('connections').subscribe({
      next: (data: any) => {
        console.log('here are the connections', data);
        this.connections.set(data);

        const onlineStatuses = this.updateDevicesFromConnections(data);
      },
      error: (error: any) => console.error('Error in connections stream:', error),
    });
    this.subscriptions.push(connectionsSub);

    const answerSub = this.socketService.on<any>('webrtcAnswer').subscribe({
      next: async (data: any) => {
        console.log('Received device answer from bot:', data);

        // Find the RTCPeerConnection for this bot
        const botId = data.botId || data.BotId; // backend should send botId
        const pc = this.peerConnections?.[botId];
        if (!pc) {
          console.error('No peer connection found for bot:', botId);
          return;
        }

        try {
          const answer: RTCSessionDescriptionInit = {
            type: data.sdp.type, // "answer"
            sdp: data.sdp.sdp,
          };
          await pc.setRemoteDescription(answer);
          const queued = this.iceCandidateQueue[botId] || [];
          for (const c of queued) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(c));
              console.log('Flushed queued ICE candidate for bot', botId);
            } catch (err) {
              console.error('Error adding queued ICE candidate:', err);
            }
          }
          delete this.iceCandidateQueue[botId];
          console.log('Remote description set for bot', botId);
        } catch (err) {
          console.error('Failed to set remote description:', err);
        }
      },
      error: (err) => console.error('deviceAnswer error:', err),
    });
    this.subscriptions.push(answerSub);

    const iceCandidateSub = this.socketService.on<any>('iceCandidate').subscribe({
      next: async (data: any) => {
        const botId = data.botId || data.BotId;
        const pc = this.peerConnections?.[botId];

        console.log('candaidate,', data);

        const candidate = new RTCIceCandidate(data.candidate);

        console.log('ice cand', candidate);

        if (!pc) {
          console.log('PeerConnection not ready, queuing ICE candidate');
          this.iceCandidateQueue[botId] = this.iceCandidateQueue[botId] || [];
          this.iceCandidateQueue[botId].push(data.candidate);
        } else if (!pc.remoteDescription) {
          // PC exists but remote description not set yet
          this.iceCandidateQueue[botId] = this.iceCandidateQueue[botId] || [];
          this.iceCandidateQueue[botId].push(data.candidate);
        } else {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch (err) {
            console.error('Error adding ICE candidate:', err);
          }
        }
      },
    });
    this.subscriptions.push(iceCandidateSub);

    const onlineStatuses = this.socketService.on<any>('onlineStatus').subscribe({
      next: (data: any) => {
        console.log('here are the connections new ', data);
        const onlineStatuses = mapOnlineData(data);
        if (onlineStatuses) {
          console.log('here are the mapped devices', onlineStatuses);
          this.connections.set(onlineStatuses);
          this.updateDevicesFromConnections(onlineStatuses);
        }
      },
      error: (error: any) => console.error('Error in connections stream:', error),
    });

    // Subscribe to credentials (for game connections)
    const credentialsSub = this.socketService.on('credentials').subscribe({
      next: (data: any) => {
        this.credentials.set(data);
      },
    });
    this.subscriptions.push(credentialsSub);

    // Subscribe to errors
    const errorsSub = this.socketService.on<string>('error').subscribe({
      next: (error: any) => {
        this.errors.set(error);
      },
    });
    this.subscriptions.push(errorsSub);
  }

  private cleanupSubscriptions(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
    // Clear online devices when disconnected
    this.devicesService.onlineDevices.set([]);
  }

  private updateDevicesFromConnections(connections: BotConnectionEvent): void {
    console.log('device update', connections);
    // Update device statuses based on connected bots
    const currentDevices = this.devicesService.devices();
    const connectedBotIds = connections.bots.map((bot) => bot.id);

    const newOnlineDevices: Device[] = connections.bots.map((bot) => {
      return {
        id: bot.id,
        name: bot.name,
        status: 'online' as const,
        cockpits: bot.cockpits.map((cockpit) => ({
          id: cockpit.id,
          name: cockpit.name,
          deviceId: cockpit.deviceId,
        })),
        mainPhotoUrl: bot.mainPhotoUrl,
        session: {
          OwnerId: bot.session.OwnerType,
          id: bot.session.id,
          ownerId: bot.session.ownerId,
        },
        layoutId:bot.layoutId
      };
    });

    console.log('new divices', newOnlineDevices);
    this.devicesService.onlineDevices.set(newOnlineDevices);

    // Update online devices lis
  }

  async connectToBot(botId: string,ngZone: NgZone) {

    let remoteStream: MediaStream | null = null;

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    this.peerConnections[botId] = pc;

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.socketService.emit('iceCandidate', {
          botId,
          candidate: event.candidate,
        });
      }
    };

    // 2️⃣ Set up remote track (bot video)
 pc.ontrack = (event) => {
  console.log('ontrack fired');
  this.devicesService.remoteDeviceStream.set(event.streams[0])
   //remoteStream = event.streams[0]; // just save it
  //remoteVideo.srcObject = remoteStream;
}


    pc.onconnectionstatechange = () => {
  console.log('PC state:', pc.connectionState);

  if (pc.connectionState === 'connected' && remoteStream) {

    if (pc.connectionState === 'connected') {
  // poll every 2 seconds
  setInterval(() => {
    pc.getStats().then(stats => {
      stats.forEach(report => {
        if (report.type === 'inbound-rtp') {
          console.log(`bytes received: ${report.bytesReceived}`);
          console.log(`frames received: ${report.framesReceived}`);
          console.log(`frames decoded: ${report.framesDecoded}`);
        }
      });
    });
  }, 2000);
}
    // raw test video
    const testVideo = document.createElement('video');
    testVideo.autoplay = true;
    testVideo.muted = true;
    testVideo.playsInline = true;
    testVideo.style.cssText = 'position:fixed; top:0; left:0; width:320px; height:240px; z-index:9999; border:3px solid red;';
    testVideo.srcObject = remoteStream;
    document.body.appendChild(testVideo);
    testVideo.play().catch(console.error);

    // also retry your real video element
  }
};

    // 3️⃣ Create SDP offer
    const offer = await pc.createOffer({
      offerToReceiveVideo: true, // only receive video
      offerToReceiveAudio: true, // optional, if bot has audio
    });

    await pc.setLocalDescription(offer);

    // 4️⃣ Send offer to backend via WebSocket
    this.socketService.emit('connectToBot', {
      botId,
      sdp: offer,
    });
  }

  // Connect to a game
  connectToGame(botId: string, cockpits: string[], gameId: string, userToken: string): void {
    this.socketService.emit('connectToGame', {
      botId,
      cockpits,
      gameId,
      usertoken: userToken,
    });
  }
}
